import OpenAI from 'openai';
import { AI_CONFIG, MODEL_CONFIGS } from '../../../config/ai.config';
import { z } from 'zod';
import type { IndustryTemplate } from '../../../config/templates/industryTemplates';

const openai = new OpenAI({
  apiKey: AI_CONFIG.openai.apiKey,
  dangerouslyAllowBrowser: true, // Note: In production, proxy through backend
});

// Define strict schema for proposal response validation
const ProposalSectionSchema = z.object({
  title: z.string(),
  content: z.string(),
  suggestions: z.array(z.string()),
  compliance: z.array(z.object({
    rule: z.string(),
    status: z.enum(['passed', 'warning', 'failed']),
    message: z.string(),
  })),
});

const ProposalResponseSchema = z.object({
  sections: z.array(ProposalSectionSchema),
  executiveSummary: z.string(),
  technicalApproach: z.string(),
  pastPerformance: z.string(),
  pricing: z.object({
    suggested: z.number(),
    breakdown: z.array(z.object({
      category: z.string(),
      amount: z.number(),
      description: z.string(),
    })),
  }),
});

export type ProposalTemplate = z.infer<typeof ProposalResponseSchema>;
export type ProposalSection = z.infer<typeof ProposalSectionSchema>;

interface GenerateProposalParams {
  title: string;
  description: string;
  type: string;
  customInstructions?: string;
  referenceDocuments?: string[];
  tone?: 'formal' | 'technical' | 'persuasive' | 'concise';
  focusAreas?: string[];
  competitiveAdvantages?: string[];
  pastPerformance?: string[];
}

interface ProposalConfig {
  template: IndustryTemplate;
  tonePreferences: string[];
  requirements: string[];
  complianceStandards: string[];
}

export class ProposalGenerator {
  async generateProposal(
    params: GenerateProposalParams,
    config: ProposalConfig
  ): Promise<ProposalTemplate> {
    try {
      const prompt = this.buildProposalPrompt(params, config);
      const response = await openai.chat.completions.create({
        ...MODEL_CONFIGS.proposal,
        messages: [
          { role: 'system', content: this.getSystemPrompt(config) },
          { role: 'user', content: prompt }
        ],
      });

      const content = response.choices[0]?.message?.content;
      if (!content) {
        throw new Error('No content generated from AI model');
      }

      // Parse and validate the response
      const parsedResponse = this.parseProposalResponse(content);
      return parsedResponse;
    } catch (error) {
      console.error('Failed to generate proposal:', error);
      if (error instanceof z.ZodError) {
        throw new Error('Invalid proposal format received from AI model');
      }
      throw new Error(error instanceof Error ? error.message : 'Failed to generate proposal');
    }
  }

  private getSystemPrompt(config: ProposalConfig): string {
    return `You are an expert proposal writer for government contracts, specializing in ${config.template.title} proposals.
Your task is to generate a comprehensive proposal following these guidelines:

- Use a ${config.tonePreferences.join(', ')} tone
- Ensure compliance with: ${config.complianceStandards.join(', ')}
- Address all requirements: ${config.requirements.join(', ')}
- Follow industry best practices for ${config.template.title} proposals
- Provide specific, actionable suggestions for improvement
- Include detailed compliance checks for each section

Format the response as a valid JSON object matching the specified schema.`;
  }

  private buildProposalPrompt(
    params: GenerateProposalParams,
    config: ProposalConfig
  ): string {
    let prompt = `Generate a detailed proposal for:

Title: ${params.title}
Description: ${params.description}
Industry: ${config.template.title}
Tone: ${params.tone || 'formal'}

Required Sections:
${config.template.sections.map(section => `- ${section}`).join('\n')}

Focus Areas:
${params.focusAreas?.map(area => `- ${area}`).join('\n') || 'None specified'}

Competitive Advantages:
${params.competitiveAdvantages?.map(adv => `- ${adv}`).join('\n') || 'None specified'}

Past Performance:
${params.pastPerformance?.map(perf => `- ${perf}`).join('\n') || 'None specified'}`;

    if (params.customInstructions) {
      prompt += `\n\nCustom Instructions:\n${params.customInstructions}`;
    }

    if (params.referenceDocuments?.length) {
      prompt += `\n\nReference Documents:\n${params.referenceDocuments.join('\n')}`;
    }

    prompt += `\n\nEnsure the proposal:
- Follows ${config.template.title} industry standards
- Addresses compliance requirements
- Includes specific examples and metrics
- Provides clear value propositions
- Follows government contracting best practices`;

    return prompt;
  }

  private parseProposalResponse(response: string): ProposalTemplate {
    try {
      // First try to parse the JSON
      const parsed = JSON.parse(response);
      
      // Then validate against our schema
      const validated = ProposalResponseSchema.parse(parsed);
      
      return validated;
    } catch (error) {
      console.error('Failed to parse proposal response:', error);
      if (error instanceof SyntaxError) {
        throw new Error('Invalid JSON response from AI model');
      }
      throw error;
    }
  }

  async improveSection(section: ProposalSection): Promise<ProposalSection> {
    try {
      const prompt = `Improve the following proposal section:

${JSON.stringify(section, null, 2)}

Please provide suggestions for improvement and ensure compliance with government contracting standards.`;

      const response = await openai.chat.completions.create({
        ...MODEL_CONFIGS.proposal,
        messages: [{ role: 'user', content: prompt }],
      });

      const content = response.choices[0]?.message?.content;
      if (!content) {
        throw new Error('No improvement suggestions generated');
      }

      const improved = ProposalSectionSchema.parse(JSON.parse(content));
      return improved;
    } catch (error) {
      console.error('Failed to improve section:', error);
      throw new Error('Failed to improve proposal section');
    }
  }
}

export const proposalGenerator = new ProposalGenerator();